import {
  DEFAULT_REPORT_DETAIL_LOCKED_FILTERS,
  REPORT_DETAIL_LOCKED_FILTER_KEYS,
  ReportDatePickerValue,
  ReportDetailFilterOptions,
  ReportDetailLockedFilterKey,
  ReportDetailLockedFilterMap,
  ReportMainFilterLockKey,
  TicketReportingFilterValues,
} from '@vision/ui/interfaces';
import dayjs from 'dayjs';
import {
  countConfiguredTicketReportingFilters,
  deserializeTicketReportingFilters,
  EMPTY_TICKET_REPORTING_FILTERS,
  getConfiguredTicketReportingFilterKeys,
  normalizeTicketReportingFilters,
  serializeTicketReportingFilters,
} from './ticket-reporting-filter.util';

const DEFAULT_REPORT_DATE_FORMAT = 'YYYY-MM-DD';

function getDefaultReportDatePickerValue(): ReportDatePickerValue {
  return {
    fromDate: dayjs().subtract(30, 'day').startOf('day').format(DEFAULT_REPORT_DATE_FORMAT),
    toDate: dayjs().endOf('day').format(DEFAULT_REPORT_DATE_FORMAT),
  };
}

export interface ReportDetailMainFilters extends TicketReportingFilterValues {
  selectedChannelIds: string[];
  selectedFlowIds: string[];
  selectedUnitIds: string[];
  selectedTagIds: string[];
  selectedCustomerSchemaIds: string[];
  selectedCustomerSchemaValues: string[];
}

export interface SavedReportDetailMainFilters extends Partial<ReportDetailMainFilters> {
  selectedDate?: Partial<ReportDatePickerValue>;
}

type ReportDetailMainDateRegistryEntry = {
  kind: 'date';
  lockKey: 'date';
  detailKeys: ['from_date', 'to_date', 'date_range'];
  lockButtonTestId: string;
};

type ReportDetailMainValueRegistryEntry = {
  kind: 'values';
  lockKey: Exclude<ReportMainFilterLockKey, 'date'>;
  stateKey: keyof ReportDetailMainFilters;
  detailKey: keyof Pick<
    ReportDetailFilterOptions,
    'channel_ids' | 'flow_id' | 'unit_ids' | 'tags' | 'customer_schema_ids' | 'customer_schema_tag_ids'
  >;
  urlParamKey: 'channel_ids' | 'flow_ids' | 'unit_ids' | 'tag_ids' | 'customer_schema_ids' | 'customer_schema_tag_ids';
  emptyValue: string[];
  lockButtonTestId: string;
};

export const REPORT_DETAIL_MAIN_DATE_FILTER_REGISTRY: ReportDetailMainDateRegistryEntry = {
  kind: 'date',
  lockKey: 'date',
  detailKeys: ['from_date', 'to_date', 'date_range'],
  lockButtonTestId: 'button-report-main-filter-lock-date',
};

export const REPORT_DETAIL_MAIN_VALUE_FILTER_REGISTRY = [
  {
    kind: 'values',
    lockKey: 'channel_ids',
    stateKey: 'selectedChannelIds',
    detailKey: 'channel_ids',
    urlParamKey: 'channel_ids',
    emptyValue: [],
    lockButtonTestId: 'button-report-main-filter-lock-channel_ids',
  },
  {
    kind: 'values',
    lockKey: 'flow_id',
    stateKey: 'selectedFlowIds',
    detailKey: 'flow_id',
    urlParamKey: 'flow_ids',
    emptyValue: [],
    lockButtonTestId: 'button-report-main-filter-lock-flow_id',
  },
  {
    kind: 'values',
    lockKey: 'unit_ids',
    stateKey: 'selectedUnitIds',
    detailKey: 'unit_ids',
    urlParamKey: 'unit_ids',
    emptyValue: [],
    lockButtonTestId: 'button-report-main-filter-lock-unit_ids',
  },
  {
    kind: 'values',
    lockKey: 'tags',
    stateKey: 'selectedTagIds',
    detailKey: 'tags',
    urlParamKey: 'tag_ids',
    emptyValue: [],
    lockButtonTestId: 'button-report-main-filter-lock-tags',
  },
  {
    kind: 'values',
    lockKey: 'customer_schema_tag_ids',
    stateKey: 'selectedCustomerSchemaIds',
    detailKey: 'customer_schema_ids',
    urlParamKey: 'customer_schema_ids',
    emptyValue: [],
    lockButtonTestId: 'button-report-main-filter-lock-customer_schema_values',
  },
  {
    kind: 'values',
    lockKey: 'customer_schema_tag_ids',
    stateKey: 'selectedCustomerSchemaValues',
    detailKey: 'customer_schema_tag_ids',
    urlParamKey: 'customer_schema_tag_ids',
    emptyValue: [],
    lockButtonTestId: 'button-report-main-filter-lock-customer_schema_values',
  },
] as const satisfies ReadonlyArray<ReportDetailMainValueRegistryEntry>;

export const REPORT_DETAIL_MAIN_FILTER_REGISTRY = [
  REPORT_DETAIL_MAIN_DATE_FILTER_REGISTRY,
  ...REPORT_DETAIL_MAIN_VALUE_FILTER_REGISTRY,
] as const;

export const EMPTY_REPORT_DETAIL_MAIN_FILTERS: ReportDetailMainFilters = {
  selectedChannelIds: [],
  selectedFlowIds: [],
  selectedUnitIds: [],
  selectedTagIds: [],
  selectedCustomerSchemaIds: [],
  selectedCustomerSchemaValues: [],
  ...EMPTY_TICKET_REPORTING_FILTERS,
};

function isLockedFilterKey(value: string): value is ReportDetailLockedFilterKey {
  return (REPORT_DETAIL_MAIN_FILTER_REGISTRY as readonly { lockKey: ReportDetailLockedFilterKey }[]).some(
    (item) => item.lockKey === value,
  );
}

function hasOwnProperty<T extends object>(value: T, key: PropertyKey): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function getSafeArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

export function getReportDetailLockedFilterKeys(detail?: ReportDetailFilterOptions | null): ReportMainFilterLockKey[] {
  if (!Array.isArray(detail?.locked_filters)) {
    return [];
  }

  return detail.locked_filters.filter(isLockedFilterKey);
}

export function getReportDetailLockedFiltersMap(
  detail?: ReportDetailFilterOptions | null,
): ReportDetailLockedFilterMap {
  const lockedFilters = new Set(getReportDetailLockedFilterKeys(detail));

  return REPORT_DETAIL_LOCKED_FILTER_KEYS.reduce<ReportDetailLockedFilterMap>(
    (accumulator, key) => {
      accumulator[key] = lockedFilters.has(key);
      return accumulator;
    },
    {
      ...DEFAULT_REPORT_DETAIL_LOCKED_FILTERS,
    },
  );
}

export function setReportDetailLockedFilters(
  detail: ReportDetailFilterOptions = {},
  lockedFiltersMap: ReportDetailLockedFilterMap,
): ReportDetailFilterOptions {
  const lockedFilters = REPORT_DETAIL_LOCKED_FILTER_KEYS.filter((key) => lockedFiltersMap[key]);
  const nextDetail = { ...detail };

  if (lockedFilters.length > 0) {
    nextDetail.locked_filters = lockedFilters;
  } else {
    delete nextDetail.locked_filters;
  }

  return nextDetail;
}

export function getReportDetailMainDateValue(detail?: ReportDetailFilterOptions | null): ReportDatePickerValue {
  const defaultDate = getDefaultReportDatePickerValue();

  return {
    fromDate:
      typeof detail?.from_date === 'string' && detail.from_date.length > 0 ? detail.from_date : defaultDate.fromDate,
    toDate: typeof detail?.to_date === 'string' && detail.to_date.length > 0 ? detail.to_date : defaultDate.toDate,
    ...(detail?.date_range !== undefined ? { dateRange: detail.date_range ?? undefined } : {}),
  };
}

export function getReportDetailMainFilters(
  detail?: ReportDetailFilterOptions | null,
  isTicketReport = false,
): ReportDetailMainFilters {
  const filters = REPORT_DETAIL_MAIN_VALUE_FILTER_REGISTRY.reduce<ReportDetailMainFilters>(
    (accumulator, entry) => {
      accumulator[entry.stateKey] = getSafeArray(detail?.[entry.detailKey]);
      return accumulator;
    },
    {
      ...EMPTY_REPORT_DETAIL_MAIN_FILTERS,
    },
  );

  if (!isTicketReport) {
    return filters;
  }

  return {
    ...EMPTY_REPORT_DETAIL_MAIN_FILTERS,
    ...deserializeTicketReportingFilters(detail),
  };
}

export function normalizeReportDetailFilters(detail?: ReportDetailFilterOptions | null): ReportDetailFilterOptions {
  const defaultDate = getDefaultReportDatePickerValue();
  const baseDetail = detail ? { ...detail } : {};
  const normalizedDetail: ReportDetailFilterOptions = {
    ...baseDetail,
    from_date:
      typeof detail?.from_date === 'string' && detail.from_date.length > 0 ? detail.from_date : defaultDate.fromDate,
    to_date: typeof detail?.to_date === 'string' && detail.to_date.length > 0 ? detail.to_date : defaultDate.toDate,
    date_range: detail?.date_range ?? null,
    channel_types: getSafeArray(detail?.channel_types),
    flow_id: getSafeArray(detail?.flow_id),
    channel_ids: getSafeArray(detail?.channel_ids),
    tag_groups: getSafeArray(detail?.tag_groups),
    tags: getSafeArray(detail?.tags),
    unit_ids: getSafeArray(detail?.unit_ids),
    tag_group_ids: getSafeArray(detail?.tag_group_ids),
    customer_schema_ids: getSafeArray(detail?.customer_schema_ids),
    customer_schema_tag_ids: getSafeArray(detail?.customer_schema_tag_ids),
  };

  return setReportDetailLockedFilters(normalizedDetail, getReportDetailLockedFiltersMap(detail));
}

function mergeCandidateDate(
  currentDate: ReportDatePickerValue,
  candidateDate?: Partial<ReportDatePickerValue>,
): ReportDatePickerValue {
  if (!candidateDate) {
    return currentDate;
  }

  const hasDateBoundary = hasOwnProperty(candidateDate, 'fromDate') || hasOwnProperty(candidateDate, 'toDate');
  const hasDateRange = hasOwnProperty(candidateDate, 'dateRange');

  return {
    fromDate:
      typeof candidateDate.fromDate === 'string' && candidateDate.fromDate.length > 0
        ? candidateDate.fromDate
        : currentDate.fromDate,
    toDate:
      typeof candidateDate.toDate === 'string' && candidateDate.toDate.length > 0
        ? candidateDate.toDate
        : currentDate.toDate,
    ...(hasDateRange
      ? { dateRange: candidateDate.dateRange ?? undefined }
      : hasDateBoundary
        ? { dateRange: undefined }
        : currentDate.dateRange !== undefined
          ? { dateRange: currentDate.dateRange }
          : {}),
  };
}

export function mergeReportDetailMainFilters({
  currentDate,
  currentFilters,
  candidateDate,
  candidateFilters,
  lockedFilters,
  canManageMainFilters,
}: {
  currentDate: ReportDatePickerValue;
  currentFilters: ReportDetailMainFilters;
  candidateDate?: Partial<ReportDatePickerValue>;
  candidateFilters?: Partial<ReportDetailMainFilters>;
  lockedFilters?: ReportDetailLockedFilterMap;
  canManageMainFilters: boolean;
}): {
  date: ReportDatePickerValue;
  filters: ReportDetailMainFilters;
} {
  const nextDate = mergeCandidateDate(currentDate, candidateDate);
  const nextFilters = REPORT_DETAIL_MAIN_VALUE_FILTER_REGISTRY.reduce<ReportDetailMainFilters>(
    (accumulator, entry) => {
      accumulator[entry.stateKey] =
        candidateFilters && hasOwnProperty(candidateFilters, entry.stateKey)
          ? [...(candidateFilters[entry.stateKey] ?? [])]
          : currentFilters[entry.stateKey];
      return accumulator;
    },
    {
      ...EMPTY_REPORT_DETAIL_MAIN_FILTERS,
    },
  );
  Object.assign(
    nextFilters,
    normalizeTicketReportingFilters({
      ...currentFilters,
      ...(candidateFilters ?? {}),
    }),
  );
  if (canManageMainFilters) {
    return {
      date: nextDate,
      filters: nextFilters,
    };
  }

  const lockedLookup = lockedFilters ?? DEFAULT_REPORT_DETAIL_LOCKED_FILTERS;
  const unlockedFilters = REPORT_DETAIL_MAIN_VALUE_FILTER_REGISTRY.reduce<ReportDetailMainFilters>(
    (accumulator, entry) => {
      accumulator[entry.stateKey] = lockedLookup[entry.lockKey]
        ? currentFilters[entry.stateKey]
        : nextFilters[entry.stateKey];
      return accumulator;
    },
    {
      ...EMPTY_REPORT_DETAIL_MAIN_FILTERS,
    },
  );
  Object.assign(unlockedFilters, normalizeTicketReportingFilters(nextFilters));

  return {
    date: lockedLookup.date ? currentDate : nextDate,
    filters: unlockedFilters,
  };
}

export function serializeReportDetailMainFilters({
  detail,
  date,
  filters,
  isTicketReport = false,
  locks,
}: {
  detail?: ReportDetailFilterOptions | null;
  date: ReportDatePickerValue;
  filters: ReportDetailMainFilters;
  isTicketReport?: boolean;
  locks: ReportDetailLockedFilterMap;
}): ReportDetailFilterOptions {
  const nextDetail: ReportDetailFilterOptions = {
    ...(detail ?? {}),
    from_date: date.fromDate,
    to_date: date.toDate,
    date_range: date.dateRange ?? null,
  };

  if (isTicketReport) {
    Object.assign(
      nextDetail,
      serializeTicketReportingFilters(filters, getConfiguredTicketReportingFilterKeys(filters)),
    );
  } else {
    REPORT_DETAIL_MAIN_VALUE_FILTER_REGISTRY.forEach((entry) => {
      nextDetail[entry.detailKey] = [...filters[entry.stateKey]];
    });
  }
  return setReportDetailLockedFilters(nextDetail, locks);
}

export function countSelectedReportDetailMainFilters(filters: ReportDetailMainFilters): number {
  return (
    REPORT_DETAIL_MAIN_VALUE_FILTER_REGISTRY.filter((entry) => filters[entry.stateKey].length > 0).length +
    countConfiguredTicketReportingFilters(getConfiguredTicketReportingFilterKeys(filters), filters)
  );
}

export function hasClearableReportDetailMainFilters({
  filters,
  locks,
  canManageMainFilters,
}: {
  filters: ReportDetailMainFilters;
  locks: ReportDetailLockedFilterMap;
  canManageMainFilters: boolean;
}): boolean {
  return (
    countConfiguredTicketReportingFilters(getConfiguredTicketReportingFilterKeys(filters), filters) > 0 ||
    REPORT_DETAIL_MAIN_VALUE_FILTER_REGISTRY.some((entry) => {
      if (filters[entry.stateKey].length === 0) {
        return false;
      }

      return canManageMainFilters || !locks[entry.lockKey];
    })
  );
}

export function getReportDetailMainFiltersStorageKey(userId: string, reportId: string): string {
  return `auto-saved-filters-${userId}-${reportId}`;
}

export function sanitizeSavedReportDetailMainFilters(value: unknown): SavedReportDetailMainFilters | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const rawValue = value as Record<string, unknown>;
  const selectedDate =
    rawValue.selectedDate && typeof rawValue.selectedDate === 'object'
      ? (rawValue.selectedDate as Record<string, unknown>)
      : null;

  return {
    selectedChannelIds: getSafeArray(rawValue.selectedChannelIds),
    selectedFlowIds: getSafeArray(rawValue.selectedFlowIds),
    selectedUnitIds: getSafeArray(rawValue.selectedUnitIds),
    selectedTagIds: getSafeArray(rawValue.selectedTagIds),
    selectedCustomerSchemaIds: getSafeArray(rawValue.selectedCustomerSchemaIds),
    selectedCustomerSchemaValues: getSafeArray(rawValue.selectedCustomerSchemaValues),
    ...normalizeTicketReportingFilters(rawValue),
    ...(selectedDate
      ? {
          selectedDate: {
            ...(typeof selectedDate.fromDate === 'string' && selectedDate.fromDate.length > 0
              ? { fromDate: selectedDate.fromDate }
              : {}),
            ...(typeof selectedDate.toDate === 'string' && selectedDate.toDate.length > 0
              ? { toDate: selectedDate.toDate }
              : {}),
            ...(hasOwnProperty(selectedDate, 'dateRange')
              ? { dateRange: selectedDate.dateRange as ReportDatePickerValue['dateRange'] | null }
              : {}),
          },
        }
      : {}),
  };
}
